import React from 'react';
import { Video, PlayCircle, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

const Watch = () => {
  const replayCategories = [
    {
      id: 'fundamentals',
      title: 'Position Play Fundamentals',
      description: 'Learn how position impacts your decisions',
      thumbnail: 'https://images.unsplash.com/photo-1511193311914-0346f16efe90?w=400&h=250&fit=crop',
      duration: '24:15',
      locked: false
    },
    {
      id: 'reading',
      title: 'Reading Your Opponents',
      description: 'Identify betting patterns and player tells',
      thumbnail: 'https://images.unsplash.com/photo-1541278107931-e006523892df?w=400&h=250&fit=crop',
      duration: '31:42',
      locked: false
    },
    {
      id: 'bankroll',
      title: 'Bankroll Management 101',
      description: 'Protect your stack and play within limits',
      thumbnail: 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?w=400&h=250&fit=crop',
      duration: '18:30',
      locked: false
    },
    {
      id: 'advanced',
      title: 'Advanced Bluffing Techniques',
      description: 'Master the art of profitable bluffs',
      thumbnail: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=400&h=250&fit=crop',
      duration: '42:18',
      locked: true,
      premium: true
    },
    {
      id: 'tournament',
      title: 'Tournament Strategy Deep Dive',
      description: 'ICM, bubble play, and final table tactics',
      thumbnail: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?w=400&h=250&fit=crop',
      duration: '56:22',
      locked: true,
      premium: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F14] to-[#0A241C] py-8">
      <div className="max-w-6xl mx-auto px-4 space-y-12">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-heading font-bold mb-2 flex items-center justify-center gap-3">
            <Video className="w-10 h-10 text-[#C9A227]" />
            Watch & Learn
          </h1>
          <p className="text-foreground/60">Live streams, replays, and expert breakdowns</p>
        </div>

        {/* Live Stream Section */}
        <div className="bg-[#121923] border-2 border-[#C9A227] rounded-xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-heading font-bold flex items-center gap-3">
              <div className="relative flex items-center">
                <div className="absolute w-3 h-3 bg-red-600 rounded-full animate-pulse" />
                <div className="w-3 h-3 bg-red-600 rounded-full" />
              </div>
              Live Poker Room
            </h2>
            <Link
              to="/live"
              className="px-4 py-2 bg-[#C9A227] text-[#0B0F14] rounded-lg font-semibold hover:bg-[#E1C25B] transition-colors"
              data-testid="watch-live-link"
            >
              Watch Live →
            </Link>
          </div>
          <p className="text-foreground/70 mb-4">Join our pros for live hand analysis and Q&A sessions</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-[#0F3227] rounded-lg p-3">
              <p className="text-foreground/60">Next Session</p>
              <p className="font-bold">Monday, 8:00 PM ET</p>
            </div>
            <div className="bg-[#0F3227] rounded-lg p-3">
              <p className="text-foreground/60">Topic</p>
              <p className="font-bold">Advanced Bluffing</p>
            </div>
            <div className="bg-[#0F3227] rounded-lg p-3">
              <p className="text-foreground/60">Host</p>
              <p className="font-bold">Tom Henderson</p>
            </div>
          </div>
        </div>

        {/* Replay Library */}
        <div>
          <h2 className="text-2xl font-heading font-bold mb-6">Replay Library</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {replayCategories.map((replay) => (
              <div
                key={replay.id}
                data-testid={`replay-${replay.id}`}
                className="bg-[#121923] border border-white/10 rounded-xl overflow-hidden"
              >
                {/* Thumbnail */}
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={replay.thumbnail}
                    alt={replay.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40" />
                  
                  {/* Lock icon for premium content */}
                  {replay.locked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                      <div className="text-center">
                        <Lock className="w-12 h-12 text-[#C9A227] mx-auto mb-2" />
                        <p className="text-xs text-[#C9A227] font-semibold">Premium Only</p>
                      </div>
                    </div>
                  )}

                  {/* Duration */}
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 text-white rounded text-xs font-semibold">
                    {replay.duration}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-heading font-semibold mb-1 line-clamp-1">{replay.title}</h3>
                  <p className="text-sm text-foreground/60 line-clamp-2">{replay.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Archives Link */}
        <div className="bg-[#121923] border border-white/10 rounded-xl p-6 text-center">
          <h3 className="font-heading font-semibold mb-2">Looking for more content?</h3>
          <p className="text-sm text-foreground/60 mb-4">Check out our full archive of past streams and lessons</p>
          <Link
            to="/archives"
            className="inline-block px-6 py-3 bg-[#C9A227] text-[#0B0F14] rounded-lg font-semibold hover:bg-[#E1C25B] transition-colors"
            data-testid="view-archives-link"
          >
            View Archives →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Watch;